import {
  ChatChoice,
  ChatChoiceCreateRequest,
  ChatChoiceListResponse,
  ChatChoiceUpdateRequest,
  ChatPromptChoiceLink
} from '../types/chatChoice';
import { PaginationParams } from '../types/pagination';
import { ApiClient } from './apiClient';

export class ChatChoiceService {
  constructor(private api: ApiClient) {}

  async list(params?: PaginationParams) {
    const query: Record<string, string | number | undefined> = {
      page: params?.page ?? 1,
      limit: params?.limit ?? 100
    };

    const result = await this.api.get<ChatChoiceListResponse>('/api/chat-choices', query);
    const response =
      result.success && result.data
        ? result.data
        : {
            data: [],
            meta: {
              page: Number(params?.page ?? 1),
              limit: Number(params?.limit ?? 100),
              total: 0,
              totalPages: 0
            }
          };

    return {
      data: response.data ?? [],
      meta: response.meta ?? {
        page: 1,
        limit: 100,
        total: 0,
        totalPages: 0
      }
    };
  }

  getById(id: number) {
    return this.api
      .get<ChatChoice>(`/api/chat-choices/${id}`)
      .then(result => (result.success && result.data ? result.data : null));
  }

  create(payload: ChatChoiceCreateRequest) {
    return this.api
      .post<ChatChoice>('/api/chat-choices', payload)
      .then(result => (result.success && result.data ? result.data : null));
  }

  update(id: number, payload: ChatChoiceUpdateRequest) {
    return this.api
      .put<ChatChoice>(`/api/chat-choices/${id}`, payload)
      .then(result => (result.success && result.data ? result.data : null));
  }

  delete(id: number) {
    return this.api.delete<void>(`/api/chat-choices/${id}`).then(() => undefined);
  }

  reorderChoices(items: Array<{ id: number; order: number }>) {
    return this.api
      .put<void>('/api/chat-choices/order', items)
      .then(result => (result.success ? undefined : undefined));
  }

  reorderOptions(choiceId: number, items: Array<{ id: number; order: number }>) {
    return this.api
      .put<void>(`/api/chat-choices/${choiceId}/options/order`, items)
      .then(result => (result.success ? undefined : undefined));
  }

  getByPrompt(promptId: number) {
    return this.api
      .get<ChatChoice[]>(`/api/chat-choices/by-prompt/${promptId}`)
      .then(result => (result.success && result.data ? result.data : []));
  }

  replacePromptLinks(promptId: number, links: ChatPromptChoiceLink[]) {
    return this.api
      .put<void>(`/api/chat-choices/by-prompt/${promptId}`, links)
      .then(result => (result.success ? undefined : undefined));
  }

  invalidateCache() {
    return this.api
      .post<void>('/api/chat-choices/invalidate-cache', {})
      .then(result => (result.success ? undefined : undefined));
  }
}
